"use client";

import { useEffect, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";

import { Button } from "@/src/components/ui/Button";
import { Input } from "@/src/components/ui/Input";

// Must match --animate-placeholder-roll in theme.css.
const ROTATE_MS = 2600;
const PLACEHOLDER_WORDS = ["Blogs", "Keywords", "Titles", "Topics", "Authors"];

interface BlogSearchProps {
  value: string;
  onChange: (value: string) => void;
}

// Styled to match SubscribeForm's underline input. The native placeholder is
// replaced by an overlay ("Search" + a word that rolls through
// PLACEHOLDER_WORDS) so the word can animate; it hides once the user types.
export function BlogSearch({ value, onChange }: BlogSearchProps) {
  const [wordIndex, setWordIndex] = useState(0);
  const isEmpty = value.length === 0;

  useEffect(() => {
    if (!isEmpty) return;
    const id = setInterval(
      () => setWordIndex((i) => (i + 1) % PLACEHOLDER_WORDS.length),
      ROTATE_MS,
    );
    return () => clearInterval(id);
  }, [isEmpty]);

  return (
    <form
      role="search"
      onSubmit={(e) => e.preventDefault()}
      className="group relative flex items-end gap-4 border-b border-neutral-300 pb-3"
    >
      <div className="relative flex-1">
        <Input
          type="search"
          name="q"
          aria-label="Search blogs"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-none border-0 bg-transparent p-0 text-base leading-6 focus:ring-0 [&::-webkit-search-cancel-button]:hidden"
        />

        {isEmpty && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center gap-1.5 overflow-hidden text-base leading-6 text-neutral-400"
          >
            Search
            <div
              key={wordIndex}
              className="animate-placeholder-roll motion-reduce:animate-none"
            >
              {PLACEHOLDER_WORDS[wordIndex]}
            </div>
          </div>
        )}
      </div>

      <Button
        unstyled
        type="submit"
        aria-label="Search"
        className="h-fit text-neutral-700 transition-colors duration-300 hover:text-brand px-7"
      >
        <MagnifyingGlass size={22} weight="regular" />
      </Button>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -bottom-px h-px origin-left scale-x-0 bg-neutral-500 transition-transform duration-400 ease-out group-hover:scale-x-100 group-focus-within:scale-x-100"
      />
    </form>
  );
}
